import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import CreateGroupModal from "../components/CreateGroupModal";
import { ChatSidebar } from "../components/chat/ChatSidebar";
import { ChatHeader, MessageList, Composer } from "../components/chat/ChatPanel";
import { useChatLists, useChannelMessages } from "../components/chat/useChatData";
import { messagesUrl } from "../components/chat/chatUtils";
import { ScanBackdrop } from "../components/scanner/ScanBackdrop";

export default function Chat() {
  const { kind, id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { hubs, dms, groups, setGroups, mentions } = useChatLists(kind, id);
  const [messages, setMessages] = useChannelMessages(kind, id);
  const [tab, setTab] = useState("hubs");
  const [text, setText] = useState("");
  const [uploading, setUploading] = useState(false);
  const [hubDraft, setHubDraft] = useState("");
  const [createGroupOpen, setCreateGroupOpen] = useState(false);

  const group = kind === "group" ? groups.find((x) => x.group_id === id) : null;
  const title = kind === "hub" ? `# ${id}` : kind === "group" ? (group?.name || "Group") : (dms.find((d) => d.user_id === id)?.name || "Traveler");
  const subtitle = kind === "hub" ? "Global Transit Hub" : kind === "group" ? (group ? `Group · ${(group.members || []).length} members` : "Group") : "Private DM";

  const send = async (imagePath) => {
    if (!text.trim() && !imagePath) return;
    try {
      const res = await api.post(messagesUrl(kind, id), { text: text.trim(), image_path: imagePath || null });
      setMessages((m) => [...m, res.data]);
      setText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    }
  };

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!["image/jpeg", "image/png"].includes(f.type)) { toast.error("Only JPG/PNG allowed"); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const res = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      await send(res.data.storage_path);
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="mt-4 sm:mt-6 grid md:grid-cols-[280px_1fr] gap-6 h-[calc(100dvh-140px)] sm:h-[calc(100vh-160px)] relative z-10">
      {kind === "hub" && <ScanBackdrop city={id} />}
      <ChatSidebar
        className={kind ? "hidden md:flex" : "flex"}
        tab={tab} setTab={setTab} kind={kind} id={id}
        hubs={hubs} dms={dms} groups={groups} mentions={mentions}
        onCreateGroup={() => setCreateGroupOpen(true)}
        onJoinHub={(c) => nav(`/chat/hub/${encodeURIComponent(c.toLowerCase())}`)}
        hubDraft={hubDraft} setHubDraft={setHubDraft}
      />

      <section className={`glass rounded-2xl flex-col min-h-0 overflow-hidden ${kind ? "flex" : "hidden md:flex"}`}>
        {!kind ? (
          <div className="flex-1 flex items-center justify-center text-center px-8" data-testid="chat-empty-state">
            <div>
              <MessageCircle size={40} className="text-[#C8A96E] mx-auto" />
              <h3 className="font-heading text-2xl mt-4">Enter a channel</h3>
              <p className="text-sm text-[#A89C98] mt-2 max-w-xs">Pick a Global Transit Hub or a Traveler DM to begin. Photos welcome — links, videos and audio are blocked for safety.</p>
            </div>
          </div>
        ) : (
          <>
            <ChatHeader
              kind={kind} id={id} group={group} title={title} subtitle={subtitle}
              isCreator={!!group && group.creator_id === user?.user_id}
              onAvatarUpdated={(avatar) => setGroups((cur) => cur.map((x) => (x.group_id === id ? { ...x, avatar } : x)))}
            />
            <MessageList messages={messages} kind={kind} user={user} />
            <Composer text={text} setText={setText} onSend={send} onUpload={onFileChange} uploading={uploading} />
          </>
        )}
      </section>
      <CreateGroupModal
        open={createGroupOpen}
        onClose={() => setCreateGroupOpen(false)}
        onCreated={(g) => {
          setGroups((cur) => [g, ...cur]);
          setTab("groups");
          nav(`/chat/group/${g.group_id}`);
        }}
      />
    </div>
  );
}
